/**
 * Recovery helpers for Next.js / webpack "ChunkLoadError" failures.
 *
 * Why this exists:
 * The store is a Next.js standalone server that serves its own `_next/static`
 * chunks from ephemeral pods. Chunk filenames are content-hashed per build, so
 * every deploy produces new hashes and the old chunk files disappear when the
 * old pods terminate. A client that loads an HTML document from one build (a
 * lingering tab, a CDN-cached page, or a request load-balanced to a pod from a
 * different build during a rolling deploy) then requests a chunk hash that no
 * longer exists on the serving pod and gets a 404. webpack turns that into a
 * `ChunkLoadError`, which bubbles to the React error boundary and crashes the
 * whole store.
 *
 * A single hard reload pulls a fresh HTML document for the current build, whose
 * chunk references match what the pods actually serve, so the page recovers.
 * These helpers detect chunk-load failures and perform exactly one guarded,
 * cache-busting reload to avoid an infinite reload loop.
 */

import { connectionLossFeature } from '@/lib/connection/connectionLossFeature';

const RELOAD_TS_KEY = 'ac:chunk-reload-ts';
const CACHE_BUST_PARAM = '_cb';

/** Minimum gap between automatic reloads, guards against reload loops. */
export const RELOAD_COOLDOWN_MS = 15_000;

const CHUNK_ERROR_PATTERNS: RegExp[] = [
    /Loading chunk [\w-]+ failed/i,
    /Loading CSS chunk [\w-]+ failed/i,
    /ChunkLoadError/i,
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
    /'text\/html' is not a valid JavaScript MIME type/i,
];

function messageFromError(error: unknown): string {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error instanceof Error) return `${error.name}: ${error.message}`;
    if (typeof error === 'object') {
        const maybe = error as { name?: unknown; message?: unknown };
        const name = typeof maybe.name === 'string' ? maybe.name : '';
        const message = typeof maybe.message === 'string' ? maybe.message : '';
        return `${name}: ${message}`;
    }
    return '';
}

/** True when the given error looks like a failed JS/CSS chunk or dynamic import. */
export function isChunkLoadError(error: unknown): boolean {
    if (!error) return false;
    if (typeof error === 'object' && (error as { name?: unknown }).name === 'ChunkLoadError') {
        return true;
    }
    const message = messageFromError(error);
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/** True when a failed resource (<script>/<link>) points at a Next.js static chunk. */
export function isStaticChunkUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return /\/_next\/static\//.test(url);
}

function readLastReloadTs(): number {
    try {
        return Number(window.sessionStorage.getItem(RELOAD_TS_KEY)) || 0;
    } catch {
        return 0;
    }
}

function writeLastReloadTs(ts: number): void {
    try {
        window.sessionStorage.setItem(RELOAD_TS_KEY, String(ts));
    } catch {
        /* sessionStorage unavailable (private mode / sandboxed) — best effort */
    }
}

/**
 * Perform a single guarded, cache-busting reload to recover from a stale-chunk
 * deploy. Returns true if a reload was triggered, false if suppressed by the
 * cooldown guard. `now` is injectable for tests.
 */
export function recoverFromChunkError(now: number = Date.now()): boolean {
    if (typeof window === 'undefined') return false;

    if (
        connectionLossFeature.isEnabled() &&
        typeof navigator !== 'undefined' &&
        !navigator.onLine
    ) {
        armReconnectRecovery();
        return false;
    }

    const last = readLastReloadTs();
    if (last && now - last < RELOAD_COOLDOWN_MS) {
        return false;
    }
    writeLastReloadTs(now);

    try {
        const url = new URL(window.location.href);
        // Bust any CDN/browser-cached HTML still referencing the dead chunk.
        url.searchParams.set(CACHE_BUST_PARAM, String(now));
        window.location.replace(url.toString());
    } catch {
        window.location.reload();
    }
    return true;
}

function armReconnectRecovery(): void {
    window.addEventListener('online', handleReconnect);
}

function handleReconnect(): void {
    if (recoverFromChunkError()) return;

    const remaining = RELOAD_COOLDOWN_MS - (Date.now() - readLastReloadTs());
    if (remaining <= 0) return;
    window.setTimeout(() => recoverFromChunkError(), remaining);
}

/**
 * Strip the cache-bust marker from the address bar after a successful boot so it
 * does not linger in the URL or leak into analytics / shared links.
 */
export function cleanupChunkRecoveryUrl(): void {
    if (typeof window === 'undefined') return;
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(CACHE_BUST_PARAM)) return;
        url.searchParams.delete(CACHE_BUST_PARAM);
        const search = url.searchParams.toString();
        window.history.replaceState(
            window.history.state,
            '',
            `${url.pathname}${search ? `?${search}` : ''}${url.hash}`,
        );
    } catch {
        /* best effort */
    }
}
